/**
 * Hand the served <head> tags over to Helmet, so there is exactly one of each.
 *
 * DYN-260. `index.html` carries a static description and a static OpenGraph
 * card. Twenty page components also render a description through Helmet, and
 * Helmet does not know the static ones exist: it manages only the tags it
 * stamped with `data-rh`. So every one of those pages served TWO descriptions,
 * and nothing decides which one a crawler uses.
 *
 * WHY NOT JUST DELETE THE STATIC TAGS. Wave 65 added them on purpose and the
 * reason is written in `index.html`: there is no prerendering, so an unfurler
 * that does not execute JavaScript sees no Helmet tag at all. Measured at the
 * time on the deployed site, zero og and zero twitter tags on five routes. The
 * static tags are the only thing those consumers ever see, and deleting them
 * trades a duplicate tag for no tag.
 *
 * WHY NOT RESTATE THEM AS DEFAULTS IN THE APP. That is two copies of the same
 * sentence, and the copy in `index.html` is the one the claims scanner already
 * reads. A second copy is a place for the wording to drift out from under a
 * check that is looking somewhere else, which is exactly how wave 69's shared
 * ribbon string hid from the scanner.
 *
 * SO THE TAGS ARE ADOPTED RATHER THAN COPIED. At boot, before React renders,
 * the served tags are read out of the head and handed to an app-level Helmet as
 * its defaults. `index.html` stays the only place the wording lives. A consumer
 * that runs no JavaScript reads the served tags as before, and a consumer that
 * does gets the page's own tag where the page sets one and the served default
 * where it does not.
 *
 * THEY ARE STAMPED, NOT REMOVED, and that is the difference between this
 * version and the one a reviewer blocked. Removing them here is synchronous
 * while Helmet writes on an animation frame, so between the two the head had
 * NEITHER the served tags nor Helmet's. A crawler that runs JavaScript under a
 * time budget, or a render that throws, would then see no description at all,
 * which is worse than the duplicate this exists to fix: partial execution used
 * to degrade to the served tags and would have degraded to nothing.
 *
 * Instead each adopted tag gets Helmet's own `data-rh` marker and stays where
 * it is. Helmet treats every `data-rh` node as its own previous output and
 * reconciles it on the next flush, whether or not Helmet created it. Measured
 * before relying on it: a stamped served tag plus a page tag of the same
 * identity leaves exactly one node afterwards, carrying the page's content. So
 * the head holds the served value until the moment it holds the real one.
 *
 * THE ENUMERATION IS ON THE EXEMPT SIDE. Every served meta is adopted unless
 * it is named below with a reason. A list of tags to ADOPT would silently miss
 * the day somebody adds a static `robots` tag that a page also sets, and that
 * gap ships as another duplicate nobody sees. A gap in the exempt list instead
 * adopts a tag it should not have, which is loud.
 */

/** Meta tags the app must NOT take over, each with the reason it is excluded. */
export const NOT_ADOPTED: Record<string, string> = {
  viewport:
    'Layout, not metadata, and it is read before first paint. Adopting it ' +
    'means the head has no viewport between removal and the frame Helmet ' +
    'writes on, which is a visible reflow on a phone rather than a tag nobody ' +
    'sees.',
  'theme-color':
    'Browser chrome, not metadata. Same first-paint argument as viewport, and ' +
    'no page sets one, so there is nothing for it to duplicate.',
};

// `charset` is NOT listed above, and that is the correction rather than an
// omission. `<meta charset="utf-8">` has neither a `name` nor a `property`, so
// the key guard in `adoptServedHead` skips it before any exemption is
// consulted. Listing it read as a considered decision while matching nothing,
// which is worse than not listing it: an exemption nothing exercises is a name
// in a list that cannot be argued with. A test pins that it survives.


export interface AdoptedTag {
  /** Exactly one of these is set, matching how the served tag was written. */
  name?: string;
  property?: string;
  content: string;
}

const keyOf = (el: Element): string | null =>
  el.getAttribute('name') ?? el.getAttribute('property');

/**
 * Hand the served meta tags to Helmet and return them for it to render.
 *
 * Call ONCE, before React renders. A second call returns an empty list rather
 * than throwing, because the tags are marked by then and marked tags are
 * skipped, so a double call is a silent no-op instead of a crash.
 */
export function adoptServedHead(doc: Document = document): AdoptedTag[] {
  const adopted: AdoptedTag[] = [];

  Array.from(doc.head.querySelectorAll('meta')).forEach((el) => {
    // A tag Helmet wrote is already managed. Adopting it would hand Helmet its
    // own output back as a default, which is how a stale description outlives
    // the page that set it.
    if (el.hasAttribute('data-rh')) return;

    const key = keyOf(el);
    // `hasOwnProperty`, not `in`. A reviewer caught that `in` also answers true
    // for `constructor`, `toString` and `__proto__`, so a served tag with one
    // of those names would have been treated as exempt while no reason for it
    // existed anywhere. Absurd as a tag name and free to rule out.
    if (key === null || Object.prototype.hasOwnProperty.call(NOT_ADOPTED, key)) return;

    const content = el.getAttribute('content');
    // A meta with no content says nothing. Adopting it would render an empty
    // tag that overrides nothing and reads as a tag being present.
    if (content === null || content.trim() === '') return;

    adopted.push(
      el.hasAttribute('property') ? { property: key, content } : { name: key, content }
    );
    // Helmet's marker. The node stays in the head and Helmet removes it on its
    // own first flush, so there is no moment with neither tag.
    el.setAttribute('data-rh', 'true');
  });

  return adopted;
}
